import { atom } from 'jotai'
import type { UIMessage } from 'ai'
import type { PageContext } from '@deepguide-ai/types'

export const DEFAULT_SCREENATA_CHAT_WIDTH_PX = 390

/**
 * Page context that Screenata pages set to give the workspace chat
 * awareness of what the user is currently viewing.
 *
 * Each page is responsible for setting this atom on mount and
 * clearing it (set to null) on unmount so stale context is never sent.
 */
export type ScreenataPageContext = PageContext

export const screenataPageContextAtom = atom<ScreenataPageContext | null>(null)

/**
 * Override the workspace chat panel configuration.
 * When set, ScreenataWorkspaceChat switches to the specified API endpoint,
 * requestBody, etc. Used by the policy regenerate split pane to reuse the
 * right-side chat panel instead of embedding its own.
 *
 * The page is responsible for setting this on mount and clearing on unmount.
 */
export interface ChatPanelOverride {
  api: string
  requestBody: Record<string, unknown>
  /**
   * Auto-start trigger. String = send that text. Object form lets callers
   * attach a per-call `body` override to the first send.
   */
  autoStartMessage?:
    | string
    | { message: string; body?: Record<string, unknown> }
  onMessagesChange?: (messages: UIMessage[]) => void
  /** Called when the AI stream finishes (server has completed all post-processing) */
  onStreamFinish?: () => void
  getPageContent?: () => string | undefined
  /** Changing this key forces the Chat component to remount */
  regenerateKey?: number
  hideHeader?: boolean
  /** When true, only send the last user message (default: true) */
  sendOnlyLastMessage?: boolean
}

export const chatPanelOverrideAtom = atom<ChatPanelOverride | null>(null)

/**
 * Dispatch a programmatic message to the active Vera chat panel.
 * Set a value to send (or prefill with draft: true); the chat panel picks it
 * up and resets to null.
 * `text` is shown to the user; `activityContext` and `body` are sent as
 * hidden request metadata. `metadata` is attached to the UIMessage itself so
 * server routing can recover intent even if a default chat instance consumes
 * the programmatic message before an override remounts.
 */
export const chatProgrammaticMessageAtom = atom<{
  text: string
  /** Prefill the composer without sending; the user reviews and submits. */
  draft?: boolean
  activityContext?: Record<string, unknown>
  body?: Record<string, unknown>
  metadata?: Record<string, unknown>
} | null>(null)


/**
 * Policy sidebar items shown during the onboarding policy phase.
 * Set by OnboardingChat when transitioning to policy generation.
 */
export interface OnboardingPolicyItem {
  slug: string
  title: string
  isGenerated: boolean
  policyStatus: string
  policyDocumentId: string | null
  generationPhase: number
}

export const GENERATION_PHASE_LABELS: Record<number, string> = {
  1: 'Core Policies',
  2: 'Supporting Policies',
  3: 'Umbrella',
  4: 'Deliverables',
  5: 'Narrative',
}

export interface OnboardingPolicySidebarState {
  items: OnboardingPolicyItem[]
  activeSlug: string | null
  /** Called when user clicks a policy in the sidebar */
  onSelectPolicy: (slug: string) => void
}

export const onboardingPolicySidebarAtom = atom<OnboardingPolicySidebarState | null>(null)

/**
 * Artifact panel state during onboarding. When set, OnboardingChat renders
 * the corresponding component in the center panel and pushes the chat to
 * the right sidebar via chatPanelOverrideAtom (same machinery Phase 2 uses).
 *
 * When null, chat renders full-width in center (default Phase 1 experience).
 *
 * Set by:
 *   - Client auto-detection after first successful saveOnboardingData (onboarding-answers)
 *   - showArtifact tool dispatched from Vera
 *   - User/components directly (e.g., panel close button sets null)
 *
 * See RFC-154.
 */
export interface VendorPickerData {
  toolCallId: string
  question: string
  categories: Record<string, string[]>
  vendorLogos?: Record<string, string>
  preSelected?: string[]
  scope?: string
}

export interface ComplianceRolesData {
  toolCallId: string
  question?: string
  roles: Array<{
    key: string
    name: string
    description?: string
    assignedTo?: string | null
  }>
  scope?: string
}

export type ArtifactPanelState =
  | { type: 'onboarding-answers'; activeFieldId?: string }
  | { type: 'policy'; policyDocumentId: string; policySlug?: string }
  | { type: 'integrations' }
  | { type: 'connect-tools' }
  | { type: 'connect-github' }
  | { type: 'policy-prep' }
  | { type: 'vendor-picker'; data: VendorPickerData }
  | { type: 'compliance-roles'; data: ComplianceRolesData }
  | null

export const artifactPanelStateAtom = atom<ArtifactPanelState>(null)

/**
 * Pending tool-call input for the current turn. Set by `PresentOptionsMessage`
 * when Vera asks a question with selectable options so Chat can render a
 * chip row above the `PromptInput` and route the answer through the same
 * event bus the inline card used to drive. Cleared after submission or on
 * unmount. Null means the chat input is free-form.
 */
export interface PendingOptionsPrompt {
  toolCallId: string
  /**
   * Which tool surfaced this prompt. `presentOptions` is a deferred tool —
   * answers route back via `addToolOutput` so the model sees a structured
   * tool_result bound to its tool_use_id (no re-parsing a fresh user
   * message). `presentQuestion` still uses the legacy "send a user message
   * with the option label" flow.
   */
  toolName?: 'presentOptions' | 'presentQuestion'
  question: string
  explanation?: string | null
  placeholder?: string | null
  options: string[]
  /** Per-option structured payload from the structured presentOptions form. */
  optionPayloads?: Array<Record<string, unknown> | null> | null
  /** Per-option short description shown under the label. */
  optionDescriptions?: Array<string | null> | null
  optionsMeta?: Array<{
    effect?: string
    recommended?: boolean
    hint?: string | null
    logoUrl?: string | null
  }> | null
  recommendedIndex?: number | null
  multiSelect: boolean
  scope?: string
  activityContext?: Record<string, unknown>
}

export const pendingOptionsPromptAtom = atom<PendingOptionsPrompt | null>(null)

export const screenataAssistantOpenAtom = atom<boolean>(false)
export const screenataAssistantModeAtom = atom<'sidebar' | 'floating'>('sidebar')
export const screenataChatFullscreenAtom = atom<boolean>(false)
export const screenataSidebarChatViewAtom = atom<boolean>(false)
export const screenataSidebarChatTransitioningAtom = atom<boolean>(false)

export type ScreenataPageChatLayoutMode = 'default' | 'chat-only' | 'split'

export const screenataPageChatLayoutModeAtom = atom<ScreenataPageChatLayoutMode>('default')
